import React from 'react';
import { Alert, Button, Col, Row, Select, Space, Tag, message, Input, DatePicker } from 'antd';
import { RedoOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useInvoices } from '../../hooks';
import { DataTable } from '../../components';

const { Option } = Select;
const { RangePicker } = DatePicker;

const statusColors: Record<string, string> = {
  paid: 'success',
  pending: 'warning',
  failed: 'error',
  overdue: 'error',
  refunded: 'default',
};

const paymentMethodLabels: Record<string, string> = {
  vnpay: 'VNPay',
  momo: 'MoMo',
  bank_transfer: 'Chuyển khoản',
  other: 'Khác',
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || fallback;
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

interface InvoiceListProps {
  title?: string;
  defaultStatus?: string;
  hideQuickScope?: boolean;
}

export const InvoiceList: React.FC<InvoiceListProps> = ({
  title = 'Invoices',
  defaultStatus,
  hideQuickScope = false,
}) => {
  const navigate = useNavigate();
  const {
    invoices,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch,
    refundInvoice,
  } = useInvoices({ page: 1, limit: 20, ...(defaultStatus ? { status: defaultStatus } : {}) });

  const handleFilterChange = (key: 'status' | 'tenantId' | 'search' | 'startDate' | 'endDate', value: string | undefined) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const handleRefund = async (invoiceId: string) => {
    try {
      await refundInvoice(invoiceId);
      message.success('Refund processed successfully');
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to process refund'));
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Invoice #',
      dataIndex: 'id',
      key: 'id',
      width: 180,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenantName',
      key: 'tenantName',
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <span>{value || 'Unknown tenant'}</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>{record.tenantId}</span>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={statusColors[status] || 'default'}>{String(status || '').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Issued At',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      width: 130,
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
    },
    {
      title: 'Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 130,
      render: (method: string) => method ? paymentMethodLabels[method] || method : '-',
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 130,
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
    },
  ];

  return (
    <div>
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load invoices"
          description={error.message}
          action={
            <Button size="small" onClick={refetch}>
              Retry
            </Button>
          }
        />
      )}

      <DataTable
        title={title}
        data={invoices}
        columns={columns}
        loading={loading}
        rowKey="id"
        onRefresh={refetch}
        emptyText={defaultStatus === 'failed' ? 'No failed payments found' : 'No invoices found'}
        filters={
          <Row gutter={16}>
          <Col span={6}>
            <Input.Search
              placeholder="Search by tenant name"
              allowClear
              prefix={<SearchOutlined />}
              onSearch={(value) => handleFilterChange('search', value || undefined)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setFilters({
                    ...filters,
                    startDate: dates[0].format('YYYY-MM-DD'),
                    endDate: dates[1].format('YYYY-MM-DD'),
                    page: 1,
                  });
                } else {
                  setFilters({
                    ...filters,
                    startDate: undefined,
                    endDate: undefined,
                    page: 1,
                  });
                }
              }}
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="paid">Paid</Option>
              <Option value="pending">Pending</Option>
              <Option value="failed">Failed</Option>
              <Option value="overdue">Overdue</Option>
              <Option value="refunded">Refunded</Option>
            </Select>
          </Col>
          {!hideQuickScope && (
            <Col span={6}>
              <Select
                placeholder="Quick scope"
                allowClear
                style={{ width: '100%' }}
                value={filters.status === 'failed' ? 'failed-only' : undefined}
                onChange={(value) =>
                  handleFilterChange('status', value === 'failed-only' ? 'failed' : undefined)
                }
              >
                <Option value="failed-only">Failed Payments Only</Option>
              </Select>
            </Col>
          )}
          </Row>
        }
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: handlePageChange,
        }}
        actionColumn={{
          width: 120,
          fixed: 'right',
          actions: (record) => [
            {
              key: 'view',
              label: 'View Details',
              onClick: () => navigate(`/billing/invoices/${record.id}`),
            },
            ...(record.status === 'failed' || record.status === 'paid'
              ? [
                  {
                    key: 'refund',
                    label: 'Process Refund',
                    icon: <RedoOutlined />,
                    onClick: () => handleRefund(record.id),
                  },
                ]
              : []),
          ],
        }}
      />
    </div>
  );
};

export default InvoiceList;
